import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  Button,
  Tooltip,
} from "@mui/material";
import isGHMC, { formatDayMonthYearTime, getCurrentDateTime, getCurrentDateTime1, tableRowBg } from "../../../Common/helper/helpar";
import { GetAllTertiaryTripReport } from "../../../Apis/userCreationApi";
import { TableLoader } from "../../../Common/ShimmerLoader";
import ExcelJS from "exceljs";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EditTeritaryTripDetails from "./editTeritaryTripetails";

const getStartOfCurrentDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00`;
};

const TertiaryTripDetails = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [fromDate, setFromDate] = useState(getStartOfCurrentDate());
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);
  const [toDate, setToDate] = useState(getCurrentDateTime());
  const [dataLoading, setDataLoading] = useState(true);
  const [VehicleNumber, setVehicleNumber] = useState("");
  const [popupModal, setPopupModal] = useState(false);
  const [editTripData, setEditTripData] = useState(null);
  const isProd = isGHMC();

  const fetchData = async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    try {
      setDataLoading(true);
      const data = await GetAllTertiaryTripReport(queryParams);

      if (data && data.length > 0) {
        setRows(data);
        setFilteredRows(data);
        setDataLoading(false);
      } else {
        setRows([]);
        setFilteredRows([]);
        setDataLoading(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setDataLoading(false);
    }
  };

  const handleFilter = () => {
    const filters = {
      fromDate: fromDate || "",
      toDate: toDate || "",
      VehicleNumber: VehicleNumber || " ",
    };
    fetchData(filters);
  };

  useEffect(() => {
    handleFilter();
  }, []);

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    const options = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" };
    return date.toLocaleString("en-GB", options).replace(",", "");
  };

  const downloadAsExcel = async () => {
    const formattedData = filteredRows.map((row, index) => ({
      "S.No": row.sno || index + 1,
      "Vehicle Number": row.vehicleNumber || "N/A",
      "Driver Name": row.driverName || "N/A",
      "Departure Station": row.departureTransferStationName || "N/A",
      "Destination Station": row.destinationTransferStationName || "N/A",
      "Trip Start Date &Time": row.tripStartDateTime ? formatDayMonthYearTime(row.tripStartDateTime) : "N/A",
      "Trip End Date &Time": row.tripEndDateTime ? formatDayMonthYearTime(row.tripEndDateTime) : "N/A",
      "Start Latitude": row.tripStartLatitude || "N/A",
      "Start Longitude": row.tripStartLongitude || "N/A",
      "End Latitude": row.tripEndLatitude || "N/A",
      "End Longitude": row.tripEndLongitude || "N/A",
      "Start Address": row.beforeAddress || "N/A",
      "End Address": row.afterAddress || "N/A",
      "Vehicle ID": row.vehicleId || "N/A",
      "Vehicle UUID": row.vehicleUuid || "N/A",
      "Vehicle Type": row.vehicleTypeName || "N/A",
      "Owner Type": row.ownerTypeName || "N/A",
      "Driver Number": row.driverNumber || "N/A",
      "Net Weight": row.netWeight || "N/A",
      "Gross Weight": row.grossWeight || "N/A",
      "MSW Type": row.mswType || "N/A",
    }));

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Secondary Trip Report");

    const boldFont = { bold: true };

    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const downloadDateTime = `Secondary Trip Report Generated on: ${formattedDate}, ${formattedTime}`;

    worksheet.addRow(["", "", downloadDateTime, ""]);
    worksheet.mergeCells(1, 2, 1, 10);
    worksheet.getCell(1, 2).value = downloadDateTime;
    worksheet.getCell(1, 2).font = boldFont;
    worksheet.getCell(1, 2).alignment = { horizontal: "center" };

    worksheet.addRow(["", "", `Secondary Trip Report (${formatDateForDisplay(fromDate)} to ${toDate}) (at ${getCurrentDateTime1()})`, ""]);
    worksheet.mergeCells(2, 2, 2, 10);
    worksheet.getCell(2, 2).value = `Secondary Trip Report (${formatDateForDisplay(fromDate)} to ${toDate}) (at ${getCurrentDateTime1()})`;
    worksheet.getCell(2, 2).font = boldFont;
    worksheet.getCell(2, 2).alignment = { horizontal: "center" };

    const tableHeaderRow = worksheet.addRow([
      "S.No",
      "Vehicle Number",
      "Driver Name",
      "Departure Station",
      "Destination Station",
      "Trip Start Date &Time",
      "Trip End Date &Time",
      "Start Latitude",
      "Start Longitude",
      "End Latitude",
      "End Longitude",
      "Start Address",
      "End Address",
      "Vehicle ID",
      "Vehicle UUID",
      "Vehicle Type",
      "Owner Type",
      "Driver Number",
      "Net Weight",
      "Gross Weight",
      "MSW Type",
    ]);

    tableHeaderRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = boldFont;
      cell.alignment = { horizontal: "left" };
      if (colNumber >= 1 && colNumber <= 30) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F6B1B1" },
        };
      }
    });

    formattedData.forEach((item, index) => {
      worksheet.addRow([
        item["S.No"],
        item["Vehicle Number"],
        item["Driver Name"],
        item["Departure Station"],
        item["Destination Station"],
        item["Trip Start Date &Time"],
        item["Trip End Date &Time"],
        item["Start Latitude"],
        item["Start Longitude"],
        item["End Latitude"],
        item["End Longitude"],
        item["Start Address"],
        item["End Address"],
        item["Vehicle ID"],
        item["Vehicle UUID"],
        item["Vehicle Type"],
        item["Owner Type"],
        item["Driver Number"],
        item["Net Weight"],
        item["Gross Weight"],
        item["MSW Type"],
      ]).eachCell({ includeEmpty: true }, (cell, colNumber) => {
        if (colNumber >= 1 && colNumber <= 30) {
          cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
        }
      });
    });

    worksheet.columns = [
      { width: 5 },
      { width: 15 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 25 },
      { width: 25 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 100 },
      { width: 100 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Secondary_Trip_Report.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 100));
    setPage(0);
  };

  const handleUpdateSuccess = (updatedData) => {
    setRows(updatedData); // Update rows with new data
    setFilteredRows(updatedData); // Update filteredRows with new data
    setPopupModal(false); // Close the modal
    setEditTripData(null); // Reset edit data
  };

  const headerStyles = {
    textAlign: "center",
    border: "1px solid #ddd",
    borderRight: "none",
    padding: "10px",
    color: "#fff",
    backgroundColor: isProd ? "#043555" : "#EC1117",
  };
  const cellStyles = {
    textAlign: "center",
    border: "1px solid #ddd",
    borderRight: "none",
    padding: "10px",
    whiteSpace: "normal",
  };

  return (
    <div>
      <div style={{ width: "100%" }}>
        <div className="content">
          <div className="page-content">
            <h4 className="text-start mb-3">
              Secondary Trip Report ({formatDateForDisplay(fromDate)} to {toDate}) (at {getCurrentDateTime1()})
            </h4>
            <div className="card">
              <div className="card-body p-3">
                <div className="row align-items-end">
                  <div className="col-md-10"></div>
                  <div className="row" style={{ textAlign: "left" }}>
                    <div className="col-md-3">
                      <label>Start Date</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        max={toDate ? toDate : getCurrentDateTime()}
                        sx={{ marginRight: "20px" }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label>End Date</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        min={fromDate}
                        max={getCurrentDateTime()}
                        sx={{ marginRight: "20px" }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label>Vehicle Number</label>
                      <input
                        type="text"
                        name="vehicleTypeCode"
                        style={FilterinputStyle}
                        placeholder="Vehicle Number"
                        value={VehicleNumber}
                        onChange={(e) => {
                          setVehicleNumber(e.target.value.replace(/\s+/g, ""));
                        }}
                      />
                    </div>
                    <div className="col-md-3 flex d-flex gap-4 justify-between">
                      <div>
                        <button
                          variant="contained"
                          className={`btn mt-4 ${isProd ? "search-ghmc" : "search-swm"}`}
                          onClick={handleFilter}
                          sx={{ padding: "10px 20px" }}
                        >
                          Search
                        </button>
                      </div>
                      <div className="mt-4">
                        <button className="btn btn-excel" onClick={downloadAsExcel}>
                          <i className="fa-solid fa-file-excel me-1 icon-container"></i> Excel
                        </button>
                      </div>
                    </div>
                  </div>
                  {dataLoading ? (
                    <TableLoader height={500} />
                  ) : (
                    <Paper sx={{ width: "115%", overflow: "hidden" }}>
                      <TableContainer
                        component={Paper}
                        sx={{
                          width: "100%",
                          margin: "20px 0",
                          maxHeight: "500px",
                          overflowY: "auto",
                          boxShadow: "0px 3px 6px rgba(0,0,0,0.1)",
                          border: "1px solid #ccc",
                        }}
                      >
                        <Table stickyHeader aria-label="sticky table">
                          <TableHead
                            sx={{
                              position: "sticky",
                              top: 0,
                              zIndex: 1,
                            }}
                          >
                            <TableRow sx={{ paddingTop: "20px" }}>
                              <TableCell sx={headerStyles}>S.No</TableCell>
                              <TableCell sx={headerStyles}>Vehicle Number</TableCell>
                              <TableCell sx={headerStyles}>Driver Name</TableCell>
                              <TableCell sx={headerStyles}>Departure Station</TableCell>
                              <TableCell sx={headerStyles}>Destination Station</TableCell>
                              <TableCell sx={headerStyles}>Trip Start Date &Time</TableCell>
                              <TableCell sx={headerStyles}>Trip End Date &Time</TableCell>
                              <TableCell sx={headerStyles}>Start Latitude</TableCell>
                              <TableCell sx={headerStyles}>Start Longitude</TableCell>
                              <TableCell sx={headerStyles}>End Latitude</TableCell>
                              <TableCell sx={headerStyles}>End Longitude</TableCell>
                              <TableCell sx={headerStyles}>Start Address</TableCell>
                              <TableCell sx={headerStyles}>End Address</TableCell>
                              <TableCell sx={headerStyles}>Vehicle UUID</TableCell>
                              <TableCell sx={headerStyles}>Vehicle Type</TableCell>
                              <TableCell sx={headerStyles}>Owner Type</TableCell>
                              <TableCell sx={headerStyles}>Driver Number</TableCell>
                              <TableCell sx={headerStyles}>Net Weight</TableCell>
                              <TableCell sx={headerStyles}>Gross Weight</TableCell>
                              <TableCell sx={headerStyles}>MSW Type</TableCell>
                              <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredRows.length > 0 ? (
                              filteredRows.map((row, index) => (
                                <TableRow
                                  key={row.sno}
                                  sx={{ backgroundColor: tableRowBg(index) }}
                                >
                                  <TableCell sx={cellStyles}>{row.sno || index + 1}</TableCell>
                                  <TableCell sx={cellStyles}>{row.vehicleNumber || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.driverName || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.departureTransferStationName || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.destinationTransferStationName || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>
                                    {row.tripStartDateTime ? formatDayMonthYearTime(row.tripStartDateTime) : "N/A"}
                                  </TableCell>
                                  <TableCell sx={cellStyles}>
                                    {row.tripEndDateTime ? formatDayMonthYearTime(row.tripEndDateTime) : "N/A"}
                                  </TableCell>
                                  <TableCell sx={cellStyles}>{row.tripStartLatitude || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.tripStartLongitude || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.tripEndLatitude || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.tripEndLongitude || "N/A"}</TableCell>
                                  <TableCell sx={{ ...cellStyles, minWidth: 300 }}>{row.beforeAddress || "N/A"}</TableCell>
                                  <TableCell sx={{ ...cellStyles, minWidth: 300 }}>{row.afterAddress || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.vehicleUuid || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.vehicleTypeName || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.ownerTypeName || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.driverNumber || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.netWeight || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.grossWeight || "N/A"}</TableCell>
                                  <TableCell sx={cellStyles}>{row.mswType || "N/A"}</TableCell>
                                  <TableCell style={{ textAlign: "center", border: "1px solid #fff" }}>
                                    <Tooltip title="Edit" placement="top">
                                      <button
                                        className={`${index % 2 === 0 ? "bg-F8CCCD" : "bg-ddefff"} ${
                                          isProd ? "edit-ghmc" : "edit-swm"
                                        }`}
                                        onClick={() => {
                                          setPopupModal(true);
                                          setEditTripData(row);
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faEdit} />
                                      </button>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={20} sx={{ textAlign: "center" }}>
                                  No data available
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={filteredRows.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </Paper>
                  )}
                </div>
                {popupModal && (
                  <EditTeritaryTripDetails
                    showModal={popupModal}
                    setShow={setPopupModal}
                    startDate={fromDate}
                    endDate={toDate}
                    selectedData={editTripData}
                    onUpdateSuccess={handleUpdateSuccess} // Pass the update handler
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterinputStyle = {
  height: "38px",
  borderRadius: "4px",
  border: "1px solid #ced4da",
  paddingLeft: "10px",
  width: "100%",
};

export default TertiaryTripDetails;